import { useState } from "react";
import logoUrl from "../assets/ctsi-buffalo-logo.png";

interface Props {
  onLogin: () => void;
}

export function LoginPage({ onLogin }: Props) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (res.ok) {
        onLogin();
      } else {
        setError("Invalid username or password");
      }
    } catch {
      setError("Unable to connect to server");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#f7f8fa] relative overflow-hidden">
      {/* subtle institutional backdrop */}
      <div className="absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-[#005bbb]/[0.07] to-transparent pointer-events-none" />
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#005bbb]/[0.05] blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-sm mx-4">
        <div className="flex flex-col items-center mb-6">
          <img
            src={logoUrl}
            alt="CTSI Buffalo"
            className="w-16 h-16 rounded-2xl shadow-lg shadow-blue-900/25 mb-4"
          />
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">
            CTSI Assistant
          </h1>
          <p className="text-[13px] text-slate-400 mt-0.5">
            University at Buffalo · Clinical & Translational Science Institute
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-slate-900/[0.06] border border-slate-200/70 p-7">
          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div>
              <label className="block text-[12px] font-medium text-slate-500 mb-1.5 ml-1">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="ctsi"
                autoFocus
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#005bbb]/[0.08] focus:border-[#005bbb]/50 focus:bg-white text-[15px] text-slate-800 placeholder:text-slate-400 transition-all"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-slate-500 mb-1.5 ml-1">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#005bbb]/[0.08] focus:border-[#005bbb]/50 focus:bg-white text-[15px] text-slate-800 placeholder:text-slate-400 transition-all"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-xl px-3.5 py-2.5">
                <svg
                  className="w-4 h-4 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                  />
                </svg>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !username || !password}
              className="w-full py-2.5 bg-[#005bbb] hover:bg-[#004a96] text-white text-[15px] font-medium rounded-xl shadow-sm shadow-blue-900/20 transition-all active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <svg className="w-4 h-4 spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Signing in…
                </span>
              ) : (
                "Sign In"
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-[11px] text-slate-400 mt-5">
          For UB researchers, clinicians, and study teams
        </p>
      </div>
    </div>
  );
}
